/**
 * OtColorsSection ("OT Colours") — Settings control for the cell colour of each usual OT timing.
 *
 * Belongs to the SESSION (utils/otColors): everyone working this session ID sees the same colours, so no
 * admin access is needed. Each row is one timing → colour; an OT officer added with any other timing
 * takes the "Other timings" colour. Changes apply at once.
 */
import { useSession } from "@/contexts/SessionContext";
import { useNotification } from "@/contexts/NotificationContext";
import {
  DEFAULT_OT_COLOR_SETTINGS,
  saveOtColorSettings,
  useOtColorVersion,
  getOtColorSettings,
  type OtColorRule,
} from "@/utils/otColors";

const toInput = (hhmm: string) => `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
const fromInput = (v: string) => v.replace(":", "");

const timeStyle = { padding: "4px 6px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14 } as const;
const colorStyle = { width: 44, height: 30, padding: 0, border: "1px solid #d1d5db", borderRadius: 6, background: "none", cursor: "pointer" } as const;

export function OtColorsSection() {
  useOtColorVersion(); // re-render when the settings change
  const { rules, otherColor } = getOtColorSettings();
  const { canEdit } = useSession();
  const { notifyError } = useNotification();
  const setOtColorSettings = (next: Parameters<typeof saveOtColorSettings>[0]) => {
    if (!canEdit) return;
    saveOtColorSettings(next).catch(() => notifyError("Could not save the OT colours"));
  };

  const updateRule = (i: number, patch: Partial<OtColorRule>) =>
    setOtColorSettings({ rules: rules.map((r, k) => (k === i ? { ...r, ...patch } : r)), otherColor });

  return (
    <div>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#64748b" }}>
        Colour of an OT officer's cells by the timing they were added with. Very dark colours are lightened so the text stays readable.{!canEdit && " View-only: ask someone with edit access to change them."}
      </p>

      {rules.map((r, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <input
            disabled={!canEdit} type="time" step={900} value={toInput(r.start)} aria-label={`OT colour ${i + 1} start`}
            onChange={(e) => e.target.value && updateRule(i, { start: fromInput(e.target.value) })} style={timeStyle}
          />
          <span>–</span>
          <input
            disabled={!canEdit} type="time" step={900} value={toInput(r.end)} aria-label={`OT colour ${i + 1} end`}
            onChange={(e) => e.target.value && updateRule(i, { end: fromInput(e.target.value) })} style={timeStyle}
          />
          <input
            disabled={!canEdit} type="color" value={r.color} aria-label={`OT colour ${i + 1} colour`}
            onChange={(e) => updateRule(i, { color: e.target.value })} style={colorStyle}
          />
          <span style={{ padding: "3px 10px", borderRadius: 4, border: "1px solid #000", background: r.color, fontSize: 12, fontWeight: 600 }}>
            OT
          </span>
          <button
            disabled={!canEdit}
            onClick={() => setOtColorSettings({ rules: rules.filter((_, k) => k !== i), otherColor })}
            aria-label={`Remove OT colour ${i + 1}`}
            style={{ border: "none", background: "none", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      ))}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14, minWidth: 124 }}>Other timings</span>
        <input
          disabled={!canEdit} type="color" value={otherColor} aria-label="OT colour for other timings"
          onChange={(e) => setOtColorSettings({ rules, otherColor: e.target.value })} style={colorStyle}
        />
        <span style={{ padding: "3px 10px", borderRadius: 4, border: "1px solid #000", background: otherColor, fontSize: 12, fontWeight: 600 }}>
          OT
        </span>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          disabled={!canEdit}
          onClick={() => setOtColorSettings({
            rules: [...rules, { start: "1000", end: "1400", color: DEFAULT_OT_COLOR_SETTINGS.otherColor }], otherColor,
          })}
          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "white", cursor: "pointer", fontSize: 13 }}
        >
          + Add timing
        </button>
        <button
          disabled={!canEdit}
          onClick={() => setOtColorSettings(null)}
          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "white", cursor: "pointer", fontSize: 13 }}
        >
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
